import { useCallback, useEffect, useMemo, useRef } from 'react'
import logger from 'logger'
import { useIntl } from 'intl'
import { useReducerState } from 'hooks'
import { processError } from 'helpers'
import { useDevice } from 'device'
import { useInquire, usePaymentFlow } from 'modules/payment'
import { useUser } from 'modules/user'

import { buttonMessages } from 'components/inputs'

import usePaymentData from './usePaymentData'

import messages from './messages'


type Token = {
  id: string
}

type State = {
  token: Token
  error: Intl.Message | string
  isFetching: boolean
}

export type Error = {
  code: number
  message: string
}

type Props = {
  accessToken: string
  sellerId: string
  onError: (error: Error) => void
}

const useAmazonWidgetsModal = ({ accessToken, sellerId, onError }: Props) => {
  const intl = useIntl()
  const { isMobile } = useDevice()
  const fetchPaymentData = usePaymentData()

  const [ state, setState ] = useReducerState<State>({ token: null, error: null, isFetching: false })
  const { token, error, isFetching } = state

  const paymentFlowContext = usePaymentFlow()
  const flow = paymentFlowContext?.flow
  const shippingForm = paymentFlowContext?.shippingForm

  const { user } = useUser()
  // ATTN we try to fetch user address only if it's a payment flow and user doesn't have a primary address
  const withAddressBook = Boolean(shippingForm) && !user?.addressInfo?.shipping?.id

  const inquireContext = useInquire()

  const isPaymentFlow = Boolean(inquireContext) // or payment method update
  const total = inquireContext?.orderData?.total

  const buttonTitle = useMemo(() => {
    if (isPaymentFlow) {
      return {
        ...buttonMessages.payWithTotal,
        values: {
          total: total ? intl.formatPrice(total) : '',
        },
      }
    }

    if (flow === 'CARD_UPDATE') {
      return messages.updateMethodButton
    }

    return messages.addMethodButton
  }, [ intl, isPaymentFlow, total, flow ])

  const consentRef = useRef<boolean>(null)
  const billingAgreementIdRef = useRef<string>(null)

  const handleError = useCallback((err) => {
    const error: Error = {
      code: err.getErrorCode(),
      message: err.getErrorMessage(),
    }

    if (typeof onError === 'function') {
      onError(error)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const checkStatus = useCallback(() => {
    const billingAgreementId = billingAgreementIdRef.current
    const consent = consentRef.current
    const validationErrors = []

    if (!billingAgreementId) {
      validationErrors.push('Billing agreement ID has not been set.')
    }
    if (consent === undefined) {
      validationErrors.push('Billing consent not yet given.')
    }
    else if (!consent) {
      validationErrors.push('Billing consent not given.')
    }

    if (validationErrors.length) {
      logger.debug({ validationErrors }, `[AmazonPay] invalid status`)

      setState({ token: null })
    }
    else {
      logger.debug(`[AmazonPay] valid status`)

      const token = {
        id: billingAgreementId,
      }

      setState({ token: token })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const setConsent = useCallback((consentStatus) => {
    if (typeof consentStatus.getConsentStatus === 'undefined') {
      return
    }

    consentRef.current = consentStatus.getConsentStatus() === 'true'
    checkStatus()

    logger.debug({ consentStatus: consentRef.current }, `[AmazonPay] set consent status`)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Widgets
  const getDesign = useCallback((width, height) => {
    if (isMobile) {
      return {
        designMode: 'smartphoneCollapsible',
      }
    }

    return {
      size: {
        width: `${width}px`,
        height: `${height}px`,
      },
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initConsent = useCallback(() => {
    const opts = {
      amazonBillingAgreementId: billingAgreementIdRef.current,
      sellerId,
      design: getDesign(400, 140),
      onReady: (consentStatus) => {
        setConsent(consentStatus)
      },
      onConsent: (consentStatus) => {
        setConsent(consentStatus)
      },
      onError: handleError,
    }

    const consent = new window.OffAmazonPayments.Widgets.Consent(opts)

    consent.bind('amazon-consent')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initWallet = useCallback(() => {
    const opts: any = {
      amazonBillingAgreementId: billingAgreementIdRef.current,
      sellerId,
      design: getDesign(400, 260),
      onReady: (ref) => {
        if (!billingAgreementIdRef.current) {
          billingAgreementIdRef.current = ref.getAmazonBillingAgreementId()
          logger.debug(`[AmazonPay] setAgreement Id %j`, billingAgreementIdRef.current)
          checkStatus()
        }
      },
      onPaymentSelect: () => {
        initConsent()
        checkStatus()
      },
      onError: handleError,
    }

    const wallet = new window.OffAmazonPayments.Widgets.Wallet(opts)

    wallet.bind('amazon-wallet')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddressSyncError = useCallback((error) => {
    // ATTN We should show specific error if user selected invalid shipping address from Amazon Widget
    // https://scentbird.slack.com/archives/C0142Q1MFMM/p1595331256146400
    setState({ isFetching: false, error })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // We should validate the address user have selected in Amazon AddressBook Widget
  // bcs this address will be sent within purchaseBegin request
  const handleAddressSelect = useCallback(async () => {
    // ATTN this function is called even when user already has selected address
    setState({ isFetching: true })

    // TODO think what we should do on card update - added on 2021-09-21 by maddoger
    try {
      const { userPaymentProfile: { data, error }, countryList } = await fetchPaymentData({
        accessToken,
        token: {
          paymentMethod: 'AMAZON_PAY',
          tokenId: billingAgreementIdRef.current,
        },
      })

      if (error) {
        logger.warn(error, '[AmazonPay] amazon address validation error')

        handleAddressSyncError(messages.addressErrors.response)
        return
      }

      if (data) {
        const { phone, ...shipping } = data.shipping

        const shippingFormCountry = shippingForm?.fields.country.state.value

        // We should check that address is in the previously selected country in shipping form
        if (shippingFormCountry && shipping.country !== shippingFormCountry) {
          logger.debug(
            {
              shippingCountry: shipping.country,
              shippingFormCountry,
            },
            '[AmazonPay] amazon address is not in the MID country'
          )

          handleAddressSyncError(messages.addressErrors.invalidCountryBecauseOfMID)
          return
        }

        const countries = countryList.data.map(({ alpha2 }) => alpha2) || [ 'US' ]

        // We should check that address country is US (United States) or Canada.
        if (!countries.includes(shipping.country)) {
          logger.debug(
            {
              shippingCountry: shipping.country,
              countries,
            },
            '[AmazonPay] amazon address country is invalid'
          )

          handleAddressSyncError(messages.addressErrors.invalidCountry)
          return
        }

        if (shippingForm) {
          logger.debug(`[AmazonPay] set shipping address from amazon pay`)

          shippingForm.setValues(shipping)

          if (await shippingForm.validate()) {
            await inquireContext?.waitForData()
          }
          else {
            logger.warn(
              {
                values: shippingForm.getValues(),
                errors: shippingForm.getErrors(),
              },
              '[AmazonPay] amazon address validation error'
            )

            // TODO add error details in formatted way if needed - added on 2021-09-20 by maddoger
            handleAddressSyncError(messages.addressErrors.validation)
            return
          }
        }

        setState({ isFetching: false, error: null })
      }
    }
    catch (error) {
      processError(error)
      handleAddressSyncError(messages.addressErrors.unknown)
    }
  }, [ accessToken, intl ]) // eslint-disable-line react-hooks/exhaustive-deps

  const initAddressBook = useCallback(() => {
    if (!withAddressBook) {
      return initWallet()
    }

    const opts = {
      agreementType: 'BillingAgreement',
      sellerId,
      onReady: (ref) => {
        billingAgreementIdRef.current = ref.getAmazonBillingAgreementId()
        logger.debug(`[AmazonPay] setAgreement Id ${billingAgreementIdRef.current}`)
        checkStatus()
      },
      design: getDesign(400, 260),
      onAddressSelect: () => {
        handleAddressSelect()
        initWallet()
      },
      onError: handleError,
    }

    const addressBook = new window.OffAmazonPayments.Widgets.AddressBook(opts)

    addressBook.bind('amazon-address-book')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initAddressBook()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isButtonDisabled = Boolean(error || !token)

  return {
    buttonTitle,
    token,
    error,
    withAddressBook,
    isButtonDisabled,
    isFetching,
  }
}


export default useAmazonWidgetsModal
